import { useState, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Search, Loader2, Briefcase, Truck, Zap, GripVertical, Tag, Download } from 'lucide-react';
import { useOpportunities, OPPORTUNITY_STATUSES, Opportunity } from '@/hooks/useOpportunities';
import { OpportunityCard } from '@/components/charging/OpportunityCard';
import { OpportunityModal } from '@/components/charging/OpportunityModal';
import { QuickTagPopoverGeneric } from '@/components/charging/QuickTagPopoverGeneric';
import { OpportunitiesPipelineExport } from '@/components/charging/OpportunitiesPipelineExport';
import { OpportunitiesKanbanExport } from '@/components/charging/OpportunitiesKanbanExport';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';

// Droppable column component
function DroppableColumn({ id, children }: { id: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  
  return (
    <div 
      ref={setNodeRef} 
      className={`min-h-[200px] transition-colors ${isOver ? 'bg-primary/5 rounded-lg' : ''}`}
    >
      {children}
    </div>
  );
}

// Draggable opportunity card wrapper
function DraggableOpportunityCard({ 
  opportunity, 
  onClick,
  onTagClick
}: { 
  opportunity: Opportunity; 
  onClick: () => void;
  onTagClick: (e: React.MouseEvent) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: opportunity.id,
    data: { opportunity },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div className="flex items-start gap-1">
        <div 
          {...listeners} 
          className="p-1 cursor-grab hover:bg-muted/50 rounded mt-1 touch-none"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="flex-1" onClick={onClick}>
          <OpportunityCard opportunity={opportunity} onClick={onClick} onTagClick={onTagClick} />
        </div>
      </div>
    </div>
  );
}

export default function ChargingOpportunities() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { opportunities, loading, createOpportunity, updateOpportunity, refetch } = useOpportunities();
  const [searchQuery, setSearchQuery] = useState('');
  const [tagFilter, setTagFilter] = useState<string>('__all__');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingOpportunity, setEditingOpportunity] = useState<Opportunity | null>(null);
  const [activeOpportunity, setActiveOpportunity] = useState<Opportunity | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  
  // Refs for export templates
  const pipelineExportRef = useRef<HTMLDivElement>(null);
  const kanbanExportRef = useRef<HTMLDivElement>(null);
  
  // Quick tag popover state
  const [tagPopoverOpen, setTagPopoverOpen] = useState(false);
  const [tagPopoverPosition, setTagPopoverPosition] = useState({ x: 0, y: 0 });
  const [tagPopoverOpportunity, setTagPopoverOpportunity] = useState<Opportunity | null>(null);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  // Determine portal type from current route
  const isCrmPortal = location.pathname.startsWith('/crm');
  const basePath = isCrmPortal ? '/crm/charging/opportunities' : '/charging/opportunities';

  // Collect all available tags
  const availableTags = useMemo(() => {
    const allTags = opportunities.flatMap(o => o.tags || []);
    return [...new Set(allTags)].sort();
  }, [opportunities]);

  // Filter opportunities by search and tag
  const filteredOpportunities = opportunities.filter(opp => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = (
      opp.name.toLowerCase().includes(searchLower) ||
      opp.organization?.name?.toLowerCase().includes(searchLower) ||
      opp.contact?.first_name?.toLowerCase().includes(searchLower) ||
      opp.contact?.last_name?.toLowerCase().includes(searchLower)
    );
    const matchesTag = tagFilter === '__all__' || (opp.tags || []).includes(tagFilter);
    return matchesSearch && matchesTag;
  });

  // Group by status
  const opportunitiesByStatus = OPPORTUNITY_STATUSES.reduce((acc, status) => {
    acc[status.id] = filteredOpportunities.filter(opp => opp.status === status.id);
    return acc;
  }, {} as Record<string, Opportunity[]>);

  // Calculate totals
  const totalFleet = opportunities.reduce((sum, opp) => sum + (opp.total_fleet || 0), 0);
  const totalElectricTrucks = opportunities.reduce((sum, opp) => sum + (opp.electric_trucks || 0), 0);

  const handleCardClick = (opportunity: Opportunity) => {
    navigate(`${basePath}/${opportunity.id}`);
  };

  const handleTagClick = (opportunity: Opportunity, e: React.MouseEvent) => {
    e.stopPropagation();
    setTagPopoverOpportunity(opportunity);
    setTagPopoverPosition({ x: e.clientX, y: e.clientY });
    setTagPopoverOpen(true);
  };

  const handleSave = async (data: Partial<Opportunity> & { contact_ids?: string[] }) => {
    if (editingOpportunity) {
      await updateOpportunity(editingOpportunity.id, data);
    } else {
      await createOpportunity(data);
    }
    setEditingOpportunity(null);
  };

  const openNewModal = () => {
    setEditingOpportunity(null);
    setModalOpen(true);
  };

  // Drag handlers
  const handleDragStart = (event: DragStartEvent) => {
    const opp = opportunities.find(o => o.id === event.active.id);
    setActiveOpportunity(opp || null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveOpportunity(null);
    
    if (!over) return;

    const newStatus = String(over.id);
    const opportunity = opportunities.find(o => o.id === active.id);
    
    if (opportunity && OPPORTUNITY_STATUSES.find(s => s.id === newStatus) && opportunity.status !== newStatus) {
      await updateOpportunity(opportunity.id, { status: newStatus });
    }
  };

  // Helper to extract domain from website URL
  const extractDomain = (website: string | null | undefined): string | null => {
    if (!website) return null;
    return website.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
  };

  // Export as custom PNG assets (2 separate files)
  const handleExportPNG = async () => {
    if (isExporting) return;
    setIsExporting(true);

    const dateStr = new Date().toISOString().split('T')[0];

    try {
      // Step 1: Collect all domains without cached favicon
      const domainsToCache = opportunities
        .filter(opp => opp.organization?.website && !opp.organization?.cached_favicon_url)
        .map(opp => extractDomain(opp.organization?.website))
        .filter((domain): domain is string => !!domain);

      const uniqueDomains = [...new Set(domainsToCache)];

      // Step 2: Batch cache favicons if needed
      if (uniqueDomains.length > 0) {
        toast({ title: `Favicons cachen (${uniqueDomains.length})...` });
        try {
          await supabase.functions.invoke('batch-cache-favicons', {
            body: { domains: uniqueDomains }
          });
          // Refetch opportunities to get updated cached URLs
          await refetch();
          // Wait for React to re-render with new data
          await new Promise(r => setTimeout(r, 500));
        } catch (cacheError) {
          console.warn('Favicon caching failed, continuing with export:', cacheError);
        }
      }
      
      // Extra wait for images to be ready
      await new Promise(r => setTimeout(r, 200));

      // Helper function to wait for images to load
      const waitForImages = async (container: HTMLDivElement) => {
        const images = container.querySelectorAll('img');
        await Promise.all(
          Array.from(images).map((img) =>
            new Promise<void>((resolve) => {
              if (img.complete && img.naturalHeight > 0) {
                resolve();
                return;
              }
              const timeout = setTimeout(() => {
                img.style.display = 'none'; // Hide broken image
                resolve();
              }, 1500);
              img.onload = () => { clearTimeout(timeout); resolve(); };
              img.onerror = () => { clearTimeout(timeout); img.style.display = 'none'; resolve(); };
            })
          )
        );
      };

      // Helper function to export a ref as PNG blob
      const exportRefAsBlob = async (ref: React.RefObject<HTMLDivElement>): Promise<Blob | null> => {
        if (!ref.current) return null;
        
        // Wait for images to load
        await waitForImages(ref.current);
        
        const canvas = await html2canvas(ref.current, {
          useCORS: true,
          allowTaint: false,
          scale: 2,
          backgroundColor: '#ffffff',
        });

        return new Promise<Blob | null>((resolve) => {
          canvas.toBlob((blob) => resolve(blob));
        });
      };

      // Generate both PNGs as blobs
      const [pipelineBlob, kanbanBlob] = await Promise.all([
        exportRefAsBlob(pipelineExportRef),
        exportRefAsBlob(kanbanExportRef),
      ]);

      // Create ZIP with both files
      const zip = new JSZip();
      if (pipelineBlob) {
        zip.file(`pipeline-overzicht-${dateStr}.png`, pipelineBlob);
      }
      if (kanbanBlob) {
        zip.file(`opportunities-kanban-${dateStr}.png`, kanbanBlob);
      }

      // Download ZIP
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `opportunities-export-${dateStr}.zip`;
      link.click();
      URL.revokeObjectURL(url);

      toast({ 
        title: 'Export voltooid', 
        description: 'ZIP bestand gedownload met pipeline overzicht + kanban board' 
      });
    } catch (error) {
      toast({ 
        title: 'Export mislukt', 
        description: 'Er ging iets mis bij het exporteren.', 
        variant: 'destructive' 
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Briefcase className="h-6 w-6" />
            Opportunities
          </h1>
          <p className="text-muted-foreground">Partnerschappen en gespreksverslagen</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportPNG} disabled={isExporting || loading}>
            <Download className="h-4 w-4 mr-2" />
            {isExporting ? 'Exporteren...' : 'Export PNG'}
          </Button>
          <Button onClick={openNewModal}>
            <Plus className="h-4 w-4 mr-2" />
            Nieuwe Opportunity
          </Button>
        </div>
      </div>

      {/* Stats and Board */}
      <div className="space-y-6">
        {/* Stats - Pipeline Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Pipeline breakdown card */}
          <Card className="md:col-span-2">
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground mb-3">Pipeline Overzicht</div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                {OPPORTUNITY_STATUSES.map(status => {
                  const count = opportunities.filter(o => o.status === status.id).length;
                  return (
                    <div key={status.id} className="flex flex-col items-center text-center">
                      <div 
                        className="h-2.5 w-2.5 rounded-full mb-1.5"
                        style={{ backgroundColor: status.color }}
                      />
                      <div className="text-xl font-bold">{count}</div>
                      <div className="text-xs text-muted-foreground truncate w-full">
                        {status.label}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
          
          {/* Fleet stats - centered */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-center gap-8">
                <div className="text-center">
                  <div className="text-2xl font-bold flex items-center justify-center gap-2">
                    <Truck className="h-5 w-5" />
                    {totalFleet.toLocaleString('nl-NL')}
                  </div>
                  <div className="text-sm text-muted-foreground">Totale vloot</div>
                </div>
                <div className="w-px h-10 bg-border" />
                <div className="text-center">
                  <div className="text-2xl font-bold flex items-center justify-center gap-2 text-green-600">
                    <Zap className="h-5 w-5" />
                    {totalElectricTrucks.toLocaleString('nl-NL')}
                  </div>
                  <div className="text-sm text-muted-foreground">E-trucks</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

      {/* Search & Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Zoek op naam, organisatie of contact..."
            className="pl-10"
          />
        </div>
        
        {availableTags.length > 0 && (
          <Select value={tagFilter} onValueChange={setTagFilter}>
            <SelectTrigger className="w-[180px]">
              <Tag className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Filter op tag" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Alle tags</SelectItem>
              {availableTags.map(tag => (
                <SelectItem key={tag} value={tag}>{tag}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Kanban Board with drag and drop */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-4 min-w-max">
              {OPPORTUNITY_STATUSES.map((status) => (
                <div key={status.id} className="w-[300px] flex-shrink-0">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div 
                            className="h-3 w-3 rounded-full" 
                            style={{ backgroundColor: status.color }}
                          />
                          {status.label}
                        </div>
                        <Badge variant="secondary">
                          {opportunitiesByStatus[status.id]?.length || 0}
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <DroppableColumn id={status.id}>
                        {opportunitiesByStatus[status.id]?.map((opportunity) => (
                          <div key={opportunity.id} className="mb-3">
                            <DraggableOpportunityCard
                              opportunity={opportunity}
                              onClick={() => handleCardClick(opportunity)}
                              onTagClick={(e) => handleTagClick(opportunity, e)}
                            />
                          </div>
                        ))}
                        {opportunitiesByStatus[status.id]?.length === 0 && (
                          <div className="text-center py-8 text-muted-foreground text-sm">
                            Geen opportunities
                          </div>
                        )}
                      </DroppableColumn>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>

          <DragOverlay>
            {activeOpportunity && (
              <div className="opacity-90 rotate-2">
                <OpportunityCard 
                  opportunity={activeOpportunity} 
                  onClick={() => {}} 
                />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}
      </div> {/* End of boardRef container */}

      {/* Modal */}
      <OpportunityModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        opportunity={editingOpportunity}
        onSave={handleSave}
      />

      {/* Quick Tag Popover */}
      {tagPopoverOpportunity && (
        <QuickTagPopoverGeneric
          open={tagPopoverOpen}
          onOpenChange={setTagPopoverOpen}
          entityId={tagPopoverOpportunity.id}
          entityName={tagPopoverOpportunity.name}
          currentTags={tagPopoverOpportunity.tags || []}
          availableTags={availableTags}
          position={tagPopoverPosition}
          queryKey={['opportunities']}
          tableName="charging_opportunities"
        />
      )}

      {/* Hidden export templates */}
      <OpportunitiesPipelineExport ref={pipelineExportRef} opportunities={opportunities} />
      <OpportunitiesKanbanExport ref={kanbanExportRef} opportunities={opportunities} />
    </div>
  );
}
